import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Slider } from '@material-ui/core';
import axios from 'axios';

import Layout from './Layout/Layout';
import BatterySVG from './Assets/BatterySVG';

import { ServerImage } from '../../../server/app';

import BatteryLifeStyles from './BatteryLife.styles';

const SCALE = 2;

// Types
export interface BatteryInfo {
  Phone: string;
  EnduranceRatingHours: number;
  TalkHours: number;
  TalkHourlyConsumption: number;
  WebHours: number;
  WebHourlyConsumption: number;
  VideoHours: number;
  VideoHourlyConsumption: number;
  StandbyConsumption: number;
}

// Component
const Battery: React.FC<RouteComponentProps> = () => {
  // Local state
  const [deviceBatteryInfo, setDeviceBatteryInfo] = useState<BatteryInfo | undefined>(undefined);
  const [allDevices, setAllDevices] = useState<ServerImage[]>([]);

  const [device, setDevice] = useState<ServerImage | undefined>();
  const [deviceImage, setDeviceImage] = useState<string | undefined>();
  const [talkHours, setTalkHours] = useState(0);
  const [webHours, setWebHours] = useState(0);
  const [videoHours, setVideoHours] = useState(0);
  const [batteryPercentage, setBatteryPercentage] = useState<number | undefined>();

  const deviceRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const batteryRef = useRef<HTMLDivElement>(null);

  const calculateBatteryPercentage = (
    deviceBatteryInfo: BatteryInfo,
    talkHours: number,
    webHours: number,
    videoHours: number
  ) => {
    const sumComponent =
      talkHours * deviceBatteryInfo.TalkHourlyConsumption +
      webHours * deviceBatteryInfo.WebHourlyConsumption +
      videoHours * deviceBatteryInfo.VideoHourlyConsumption;
    const percentage = 100 - sumComponent;
    return percentage;
  };

  // Effects
  useEffect(() => {
    const getAvailableImages = async () => {
      const availableImages = await axios.get<ServerImage[]>(
        '/api/images?path=devices&battery=true'
      );
      const sortedImages = availableImages.data.sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      );

      setAllDevices(sortedImages);
    };

    getAvailableImages();
  }, []);

  useEffect(() => {
    // Retrieve the device image
    const retrieveDeviceImage = async () => {
      if (device) {
        const deviceImageToFind = allDevices.find(d => d.name === device.name);
        const deviceServer = await axios.get(
          `/api/image/?path=devices/${deviceImageToFind && deviceImageToFind.path}`,
          {
            responseType: 'blob',
          }
        );

        const imagePath = URL.createObjectURL(deviceServer.data);

        // Preload & set
        const tempImage = new Image();

        tempImage.src = imagePath;
        tempImage.onload = () => {
          setDeviceImage(imagePath);
        };
      }
    };

    const retrieveDeviceBatteryInfo = async () => {
      if (device) {
        const deviceBatteryInfoReq = await axios.get(`/api/battery?device=${device.name}`, {
          responseType: 'json',
        });
        const deviceBatteryInfo = deviceBatteryInfoReq.data;
        setDeviceBatteryInfo(deviceBatteryInfo);
      }
    };

    retrieveDeviceImage();
    retrieveDeviceBatteryInfo();
  }, [allDevices, device]);

  useEffect(() => {
    if (deviceImage) {
      const container = containerRef.current;
      const deviceEl = deviceRef.current;
      const battery = batteryRef.current;

      if (container && deviceEl && battery && device) {
        const width = (device.meta.width ?? 0) / SCALE;
        const height = (device.meta.height ?? 0) / SCALE;

        container.style.width = `${width}px`;
        container.style.height = `${height}px`;
        deviceEl.style.width = `${width}px`;
        deviceEl.style.height = `${height}px`;

        battery.style.width = `${0.5 * width}px`;

        battery.style.top = `${height / 2 - battery.offsetHeight / 2}px`;
        battery.style.left = `${width / 2 - battery.offsetWidth / 2}px`;
      }
    }
  }, [deviceImage, device]);

  useEffect(() => {
    // Only runs on initial device selection
    if (deviceBatteryInfo && !batteryPercentage) {
      const batteryPercentageLocal = calculateBatteryPercentage(
        deviceBatteryInfo,
        talkHours,
        webHours,
        videoHours
      );
      setBatteryPercentage(batteryPercentageLocal);
    }
  }, [deviceBatteryInfo, talkHours, webHours, videoHours, batteryPercentage]);

  const changeHours = (event: any, value: number, selector: 'WEB' | 'VIDEO' | 'TALK') => {
    if (batteryPercentage && deviceBatteryInfo) {
      let batteryPercentageLocal;
      if (selector === 'WEB') {
        batteryPercentageLocal = calculateBatteryPercentage(
          deviceBatteryInfo,
          talkHours,
          value,
          videoHours
        );
        setWebHours(value);
        setBatteryPercentage(batteryPercentageLocal);
      } else if (selector === 'VIDEO') {
        batteryPercentageLocal = calculateBatteryPercentage(
          deviceBatteryInfo,
          talkHours,
          webHours,
          value
        );
        setVideoHours(value);
        setBatteryPercentage(batteryPercentageLocal);
      } else {
        batteryPercentageLocal = calculateBatteryPercentage(
          deviceBatteryInfo,
          value,
          webHours,
          videoHours
        );
        setTalkHours(value);
        setBatteryPercentage(batteryPercentageLocal);
      }
    }
  };

  // useEffect(() => {
  //   if (deviceBatteryInfo) {
  //     const maxBatteryTime = calculateBatteryTime(deviceBatteryInfo, 0, 0, 0);
  //     setMaxBatteryTime(maxBatteryTime);
  //   }
  // }, [deviceBatteryInfo]);

  // Render
  return (
    <Layout>
      <BatteryLifeStyles>
        <div className="filter">
          <h2>Battery</h2>

          <div className="select">
            <Autocomplete
              disableClearable
              options={allDevices}
              getOptionLabel={image => image.name}
              onChange={(_e, value) => value && setDevice(value)}
              renderInput={params => <TextField {...params} label="Device" variant="outlined" />}
            />
          </div>

          {device && deviceBatteryInfo && (
            <>
              <div className="slider">
                <p>Talk</p>
                <div className="slider-input">
                  <span>0</span>
                  <div>
                    <Slider
                      // valueLabelFormat={value => {
                      //   const values = value.toString().split('.');
                      //   return `${values[0]}${values[1] === '5' ? ':30' : ''}`;
                      // }}
                      value={talkHours}
                      // step={1}
                      // marks
                      min={0}
                      max={8}
                      valueLabelDisplay="on"
                      onChange={(event, value) =>
                        value !== talkHours && changeHours(event, value as number, 'TALK')
                      }
                    />
                  </div>
                  <span>8hr</span>
                </div>
              </div>

              <div className="slider">
                <p>Web</p>
                <div className="slider-input">
                  <span>0</span>
                  <div>
                    <Slider
                      // valueLabelFormat={value => {
                      //   const values = value.toString().split('.');
                      //   return `${values[0]}${values[1] === '5' ? ':30' : ''}`;
                      // }}
                      value={webHours}
                      // step={1}
                      // marks
                      min={0}
                      max={8}
                      valueLabelDisplay="on"
                      onChange={(event, value) =>
                        value !== webHours && changeHours(event, value as number, 'WEB')
                      }
                    />
                  </div>
                  <span>8hr</span>
                </div>
              </div>

              <div className="slider">
                <p>Video</p>
                <div className="slider-input">
                  <span>0</span>
                  <div>
                    <Slider
                      // valueLabelFormat={value => {
                      //   const values = value.toString().split('.');
                      //   return `${values[0]}${values[1] === '5' ? ':30' : ''}`;
                      // }}
                      value={videoHours}
                      // step={1}
                      // marks
                      min={0}
                      max={8}
                      valueLabelDisplay="on"
                      onChange={(event, value) =>
                        value !== videoHours && changeHours(event, value as number, 'VIDEO')
                      }
                    />
                  </div>
                  <span>8hr</span>
                </div>
              </div>
            </>
          )}
        </div>

        {device && deviceBatteryInfo && batteryPercentage !== undefined && (
          <div className="device">
            {/* <div className="loading" hidden={!loading}>
              <CircularProgress />
            </div> */}

            <div className="container" ref={containerRef}>
              <img src={deviceImage} className="device-img" alt="" ref={deviceRef} />

              <div className="battery-icon" ref={batteryRef}>
                <BatterySVG
                  className="battery-svg"
                  height={
                    batteryPercentage < 0 ? 0 : batteryPercentage > 100 ? 100 : batteryPercentage
                  }
                />
              </div>

              <p className="endurance-rating">
                {batteryPercentage < 0
                  ? 0
                  : batteryPercentage > 100
                  ? 100
                  : batteryPercentage.toFixed(0)}
                %
              </p>

              {/* <div className="battery-life-device-1"> */}
              {/* <BatteryStdIcon className="battery-icon" /> */}
              {/* <div className="endurance-rating">{device.enduranceRating} Hours</div>
                <div className="battery-size">Battery Size: {device.mAH} mAH</div>
                {determineBatteryQuality(device.enduranceRating)} */}
              {/* </div> */}
            </div>
          </div>
        )}
      </BatteryLifeStyles>
    </Layout>
  );
};

export default Battery;
